/**
 * Register Page
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { register as registerService, validatePassword } from '../services/authService';
import '../styles/auth.css';

export default function RegisterPage() {
  const navigate = useNavigate();
  
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Validation
    if (name.length < 2) {
      setError('Name muss mindestens 2 Zeichen lang sein');
      setIsLoading(false);
      return;
    }

    const pwErr = validatePassword(password);
    if (pwErr) {
      setError(pwErr);
      setIsLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwörter stimmen nicht überein');
      setIsLoading(false);
      return;
    }

    try {
      await registerService({ name, email, password });
      setSuccess(true);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/#lab-auth');
      }, 3000);
    } catch (err: any) {
      setError(err.message || 'Registrierung fehlgeschlagen');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-success-card">
            <div className="auth-success-icon">
              <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="#22c55e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
            </div>
            <h2>Registrierung erfolgreich!</h2>
            <p>
              Wir haben Ihnen eine Bestätigungs-E-Mail an <strong>{email}</strong> gesendet.
            </p>
            <p>
              Bitte überprüfen Sie Ihr Postfach und klicken Sie auf den Bestätigungslink,
              um Ihr Konto zu aktivieren.
            </p>
            <p className="auth-redirect-notice">
              Sie werden in wenigen Sekunden zur Anmeldeseite weitergeleitet...
            </p>
            <Link to="/#lab-auth" className="auth-button" style={{ marginTop: '1rem' }}>
              Jetzt anmelden
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-header">
          <h1 className="auth-logo">
            <svg viewBox="0 0 24 26" width="28" height="30" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1L1 11v14h6v-8h10v8h6V11L12 1z" fill="rgba(55, 65, 81, 0.3)" stroke="rgba(156, 163, 175, 0.9)"/></svg>
            SurrealEstate<span className="auth-logo-accent" style={{ color: '#9ca3af' }}>Lab</span>
          </h1>
          <h2>Konto erstellen</h2>
          <p>Registrieren Sie sich für alle SurrealEstateLab-Dienste</p>
        </div>

        <form className="auth-form" onSubmit={handleSubmit}>
          {error && (
            <div className="auth-error">
              <span><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg></span>
              <p>{error}</p>
            </div>
          )}

          <div className="auth-field">
            <label htmlFor="name">Name</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ihr vollständiger Name"
              required
              autoComplete="name"
            />
          </div>

          <div className="auth-field">
            <label htmlFor="email">E-Mail-Adresse</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ihre@email.ch"
              required
              autoComplete="email"
            />
          </div>

          <div className="auth-field">
            <label htmlFor="password">Passwort</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Min. 8 Zeichen, 1 Grossbuchstabe, 1 Sonderzeichen"
              required
              autoComplete="new-password"
            />
          </div>

          <div className="auth-field">
            <label htmlFor="confirmPassword">Passwort bestätigen</label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Passwort wiederholen"
              required
              autoComplete="new-password"
            />
          </div>

          <button
            type="submit"
            className="auth-button"
            disabled={isLoading}
          >
            {isLoading ? 'Registrierung läuft...' : 'Registrieren'}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Bereits ein Konto?{' '}
            <Link to="/#lab-auth" className="auth-link">
              Jetzt anmelden
            </Link>
          </p>
          <Link to="/" className="auth-link-secondary">
            ← Zurück zur Startseite
          </Link>
        </div>
      </div>
    </div>
  );
}
